/// UK Met Office Shipping Forecast + Inshore Waters Forecast
/// terminology with the exact ranges each term covers. Sources:
///
///   - Met Office "Shipping Forecast: A Guide for the User"
///     https://www.metoffice.gov.uk/weather/specialist-forecasts/coast-and-sea/shipping-forecast
///   - "Marine Forecasting Explained" / National Meteorological Library.
///
/// Each entry pairs the term with the exact range so the flashcards
/// and reference views can teach both directions:
///   - definition ("Fresh" → "17–21 kn, Beaufort 5")
///   - reverse    ("22–27 kn" → "Strong, Beaufort 6")

export type WeatherCategory =
  | "wind"
  | "visibility"
  | "sea-state"
  | "timing"
  | "movement"
  | "pressure";

export interface WeatherTerm {
  /// Stable ID — kebab-cased version of the term. Used as the
  /// answer key for the flashcards game.
  id: string;
  /// The term itself, as it appears in a Met Office forecast.
  term: string;
  /// The category this term belongs to.
  category: WeatherCategory;
  /// Concise definition / range for use on a button face.
  shortMeaning: string;
  /// Full Met Office definition for the reference view + reveal.
  meaning: string;
}

const ENTRIES: readonly WeatherTerm[] = [
  // ─── Wind force (Beaufort scale) ───────────────────────────
  {
    id: "calm",
    term: "Calm",
    category: "wind",
    shortMeaning: "< 1 kn (Beaufort 0)",
    meaning: "Less than 1 knot. Beaufort 0. Sea like a mirror.",
  },
  {
    id: "light",
    term: "Light",
    category: "wind",
    shortMeaning: "1–10 kn (Beaufort 1–3)",
    meaning:
      "1–10 knots — Beaufort 1 (light air), 2 (light breeze) or 3 (gentle breeze).",
  },
  {
    id: "moderate-wind",
    term: "Moderate",
    category: "wind",
    shortMeaning: "11–16 kn (Beaufort 4)",
    meaning: "11–16 knots. Beaufort 4 (moderate breeze).",
  },
  {
    id: "fresh",
    term: "Fresh",
    category: "wind",
    shortMeaning: "17–21 kn (Beaufort 5)",
    meaning: "17–21 knots. Beaufort 5 (fresh breeze).",
  },
  {
    id: "strong",
    term: "Strong",
    category: "wind",
    shortMeaning: "22–33 kn (Beaufort 6–7)",
    meaning:
      "22–33 knots. Beaufort 6 (strong breeze) and 7 (near gale). Met Office combines both as 'strong' in forecasts.",
  },
  {
    id: "near-gale",
    term: "Near gale",
    category: "wind",
    shortMeaning: "28–33 kn (Beaufort 7)",
    meaning: "28–33 knots. Beaufort 7.",
  },
  {
    id: "gale",
    term: "Gale",
    category: "wind",
    shortMeaning: "34–40 kn (Beaufort 8)",
    meaning:
      "34–40 knots. Beaufort 8. Met Office issues a gale warning when winds of force 8 or more are expected.",
  },
  {
    id: "severe-gale",
    term: "Severe gale",
    category: "wind",
    shortMeaning: "41–47 kn (Beaufort 9)",
    meaning: "41–47 knots. Beaufort 9.",
  },
  {
    id: "storm",
    term: "Storm",
    category: "wind",
    shortMeaning: "48–55 kn (Beaufort 10)",
    meaning: "48–55 knots. Beaufort 10.",
  },
  {
    id: "violent-storm",
    term: "Violent storm",
    category: "wind",
    shortMeaning: "56–63 kn (Beaufort 11)",
    meaning: "56–63 knots. Beaufort 11.",
  },
  {
    id: "hurricane-force",
    term: "Hurricane force",
    category: "wind",
    shortMeaning: "≥ 64 kn (Beaufort 12)",
    meaning: "64 knots or more. Beaufort 12.",
  },

  // ─── Wind direction changes ────────────────────────────────
  // "Veering" and "Backing" describe how the wind direction is
  // changing over time, NOT the wind's current direction or
  // speed. Both appear in Met Office shipping + inshore forecasts
  // alongside the wind force, e.g. "Southwest 5 to 7, veering
  // northwest 6 to gale 8 later".
  {
    id: "veering",
    term: "Veering",
    category: "wind",
    shortMeaning: "Wind direction shifting clockwise",
    meaning:
      "The wind direction is changing CLOCKWISE — e.g. southwest → west → northwest. In the Northern Hemisphere this typically follows the passage of a cold front. Mnemonic: 'right turn' (the wind is turning to your right when looking downwind).",
  },
  {
    id: "backing",
    term: "Backing",
    category: "wind",
    shortMeaning: "Wind direction shifting anticlockwise",
    meaning:
      "The wind direction is changing ANTICLOCKWISE — e.g. southwest → south → southeast. In the Northern Hemisphere this typically precedes an approaching warm front or the warm sector of a depression. Mnemonic: 'left turn' (the wind is going back the other way relative to a veer).",
  },

  // ─── Visibility ────────────────────────────────────────────
  {
    id: "good",
    term: "Good",
    category: "visibility",
    shortMeaning: "> 5 nm",
    meaning: "More than 5 nautical miles.",
  },
  {
    id: "moderate-vis",
    term: "Moderate",
    category: "visibility",
    shortMeaning: "2–5 nm",
    meaning: "Between 2 and 5 nautical miles.",
  },
  {
    id: "poor",
    term: "Poor",
    category: "visibility",
    shortMeaning: "1000 m – 2 nm",
    meaning: "Between 1000 metres and 2 nautical miles.",
  },
  {
    id: "fog",
    term: "Fog",
    category: "visibility",
    shortMeaning: "< 1000 m",
    meaning:
      "Less than 1000 metres. 'Very poor' and 'fog' are interchangeable in Met Office bulletins.",
  },

  // ─── Sea state (WMO scale, significant wave height) ────────
  {
    id: "smooth",
    term: "Smooth",
    category: "sea-state",
    shortMeaning: "0.1–0.5 m",
    meaning: "Significant wave height 0.1–0.5 m.",
  },
  {
    id: "slight",
    term: "Slight",
    category: "sea-state",
    shortMeaning: "0.5–1.25 m",
    meaning: "Significant wave height 0.5–1.25 m.",
  },
  {
    id: "moderate-sea",
    term: "Moderate",
    category: "sea-state",
    shortMeaning: "1.25–2.5 m",
    meaning: "Significant wave height 1.25–2.5 m.",
  },
  {
    id: "rough",
    term: "Rough",
    category: "sea-state",
    shortMeaning: "2.5–4 m",
    meaning: "Significant wave height 2.5–4 m.",
  },
  {
    id: "very-rough",
    term: "Very rough",
    category: "sea-state",
    shortMeaning: "4–6 m",
    meaning: "Significant wave height 4–6 m.",
  },
  {
    id: "high",
    term: "High",
    category: "sea-state",
    shortMeaning: "6–9 m",
    meaning: "Significant wave height 6–9 m.",
  },
  {
    id: "very-high",
    term: "Very high",
    category: "sea-state",
    shortMeaning: "9–14 m",
    meaning: "Significant wave height 9–14 m.",
  },
  {
    id: "phenomenal",
    term: "Phenomenal",
    category: "sea-state",
    shortMeaning: "> 14 m",
    meaning: "Significant wave height greater than 14 m.",
  },

  // ─── Timing (relative to forecast issue time) ──────────────
  {
    id: "imminent",
    term: "Imminent",
    category: "timing",
    shortMeaning: "within 6 h",
    meaning: "Expected within 6 hours of the time of issue of the warning.",
  },
  {
    id: "soon",
    term: "Soon",
    category: "timing",
    shortMeaning: "6–12 h",
    meaning: "Expected between 6 and 12 hours of the time of issue.",
  },
  {
    id: "later",
    term: "Later",
    category: "timing",
    shortMeaning: "> 12 h",
    meaning: "Expected more than 12 hours after the time of issue.",
  },

  // ─── Movement of pressure systems ──────────────────────────
  {
    id: "slowly",
    term: "Slowly",
    category: "movement",
    shortMeaning: "< 15 kn",
    meaning: "Pressure system moving at less than 15 knots.",
  },
  {
    id: "steadily",
    term: "Steadily",
    category: "movement",
    shortMeaning: "15–25 kn",
    meaning: "Pressure system moving at 15–25 knots.",
  },
  {
    id: "rather-quickly",
    term: "Rather quickly",
    category: "movement",
    shortMeaning: "25–35 kn",
    meaning: "Pressure system moving at 25–35 knots.",
  },
  {
    id: "rapidly",
    term: "Rapidly",
    category: "movement",
    shortMeaning: "35–45 kn",
    meaning: "Pressure system moving at 35–45 knots.",
  },
  {
    id: "very-rapidly",
    term: "Very rapidly",
    category: "movement",
    shortMeaning: "> 45 kn",
    meaning: "Pressure system moving at more than 45 knots.",
  },

  // ─── Pressure tendency (over 3 hours) ──────────────────────
  {
    id: "rising-falling-slowly",
    term: "Rising / falling slowly",
    category: "pressure",
    shortMeaning: "0.1–1.5 mb / 3h",
    meaning: "Change of 0.1–1.5 millibars in the preceding 3 hours.",
  },
  {
    id: "rising-falling",
    term: "Rising / falling",
    category: "pressure",
    shortMeaning: "1.6–3.5 mb / 3h",
    meaning: "Change of 1.6–3.5 millibars in the preceding 3 hours.",
  },
  {
    id: "rising-falling-quickly",
    term: "Rising / falling quickly",
    category: "pressure",
    shortMeaning: "3.6–6.0 mb / 3h",
    meaning: "Change of 3.6–6.0 millibars in the preceding 3 hours.",
  },
  {
    id: "rising-falling-very-rapidly",
    term: "Rising / falling very rapidly",
    category: "pressure",
    shortMeaning: "> 6.0 mb / 3h",
    meaning: "Change of more than 6.0 millibars in the preceding 3 hours.",
  },
  {
    id: "now-rising-falling",
    term: "Now rising / falling",
    category: "pressure",
    shortMeaning: "trend reversal",
    meaning:
      "The pressure has been falling (or rising) but at the time of observation was definitely rising (or falling).",
  },
];

export const WEATHER_TERMS: Readonly<Record<string, WeatherTerm>> = Object.freeze(
  Object.fromEntries(ENTRIES.map((e) => [e.id, e])),
);

export const WEATHER_IDS: readonly string[] = ENTRIES.map((e) => e.id);

export const WEATHER_CATEGORIES: readonly WeatherCategory[] = [
  "wind",
  "visibility",
  "sea-state",
  "timing",
  "movement",
  "pressure",
];

/// The original Beaufort wind force scale (1805, Sir Francis
/// Beaufort). Designed for observers AT SEA without instruments —
/// each force level pairs a wind speed with the visible effect on
/// the water surface (waves, foam, spray). Modern Met Office
/// forecasts collapse Beaufort into broader categories ("Light" =
/// F1-3, "Strong" = F6, etc.), but the original force-by-force
/// reference is what skippers use to estimate wind at sea by eye.
export interface BeaufortForce {
  /// 0 — 12. The Beaufort number itself.
  force: number;
  /// Canonical descriptive name (Calm, Light air, Light breeze, …).
  name: string;
  /// Speed range in knots — closed interval [low, high]. For
  /// hurricane force the upper bound is conceptually unbounded; we
  /// encode it as a sentinel.
  knots: { low: number; high: number };
  /// Probable wave height (significant) in metres — the value the
  /// Beaufort tables give for fully-developed seas in the open
  /// ocean. Sheltered waters see lower heights at the same force.
  probableWaveHeight: string;
  /// Sea criteria — what the observer sees on the water. This is
  /// the canonical visual indicator the scale was designed around.
  seaCriteria: string;
  /// Land criteria — what the same wind does ashore. Less useful
  /// for the watch-keeper but matches the scale as published.
  landCriteria: string;
}

export const BEAUFORT_SCALE: readonly BeaufortForce[] = [
  {
    force: 0,
    name: "Calm",
    knots: { low: 0, high: 0 },
    probableWaveHeight: "—",
    seaCriteria: "Sea like a mirror.",
    landCriteria: "Smoke rises vertically.",
  },
  {
    force: 1,
    name: "Light air",
    knots: { low: 1, high: 3 },
    probableWaveHeight: "0.1 m",
    seaCriteria:
      "Ripples with the appearance of scales are formed but without foam crests.",
    landCriteria:
      "Direction shown by smoke drift but not by wind vanes.",
  },
  {
    force: 2,
    name: "Light breeze",
    knots: { low: 4, high: 6 },
    probableWaveHeight: "0.2 m",
    seaCriteria:
      "Small wavelets, still short but more pronounced; crests have a glassy appearance and do not break.",
    landCriteria:
      "Wind felt on face; leaves rustle; ordinary wind vanes moved.",
  },
  {
    force: 3,
    name: "Gentle breeze",
    knots: { low: 7, high: 10 },
    probableWaveHeight: "0.6 m",
    seaCriteria:
      "Large wavelets; crests begin to break; foam of glassy appearance; scattered white horses.",
    landCriteria:
      "Leaves and small twigs in constant motion; light flags extended.",
  },
  {
    force: 4,
    name: "Moderate breeze",
    knots: { low: 11, high: 16 },
    probableWaveHeight: "1.0 m",
    seaCriteria:
      "Small waves becoming longer; fairly frequent white horses.",
    landCriteria:
      "Raises dust and loose paper; small branches moved.",
  },
  {
    force: 5,
    name: "Fresh breeze",
    knots: { low: 17, high: 21 },
    probableWaveHeight: "2.0 m",
    seaCriteria:
      "Moderate waves taking a more pronounced long form; many white horses are formed; chance of some spray.",
    landCriteria:
      "Small trees in leaf begin to sway; crested wavelets on inland waters.",
  },
  {
    force: 6,
    name: "Strong breeze",
    knots: { low: 22, high: 27 },
    probableWaveHeight: "3.0 m",
    seaCriteria:
      "Large waves begin to form; the white foam crests are more extensive everywhere; probably some spray.",
    landCriteria:
      "Large branches in motion; whistling heard in telegraph wires; umbrellas used with difficulty.",
  },
  {
    force: 7,
    name: "Near gale",
    knots: { low: 28, high: 33 },
    probableWaveHeight: "4.0 m",
    seaCriteria:
      "Sea heaps up and white foam from breaking waves begins to be blown in streaks along the direction of the wind.",
    landCriteria:
      "Whole trees in motion; inconvenience felt when walking against the wind.",
  },
  {
    force: 8,
    name: "Gale",
    knots: { low: 34, high: 40 },
    probableWaveHeight: "5.5 m",
    seaCriteria:
      "Moderately high waves of greater length; edges of crests begin to break into spindrift; the foam is blown in well-marked streaks along the direction of the wind.",
    landCriteria:
      "Breaks twigs off trees; generally impedes progress.",
  },
  {
    force: 9,
    name: "Strong gale",
    knots: { low: 41, high: 47 },
    probableWaveHeight: "7.0 m",
    seaCriteria:
      "High waves; dense streaks of foam along the direction of the wind; crests of waves begin to topple, tumble and roll over; spray may affect visibility.",
    landCriteria:
      "Slight structural damage occurs — chimney pots and slates removed.",
  },
  {
    force: 10,
    name: "Storm",
    knots: { low: 48, high: 55 },
    probableWaveHeight: "9.0 m",
    seaCriteria:
      "Very high waves with long overhanging crests; the resulting foam in great patches is blown in dense white streaks; the surface of the sea takes a white appearance; the tumbling of the sea becomes heavy and shock-like; visibility affected.",
    landCriteria:
      "Seldom experienced inland; trees uprooted; considerable structural damage.",
  },
  {
    force: 11,
    name: "Violent storm",
    knots: { low: 56, high: 63 },
    probableWaveHeight: "11.5 m",
    seaCriteria:
      "Exceptionally high waves (small and medium-sized ships might for a long time be lost from view behind the waves); the sea is completely covered with long white patches of foam lying along the direction of the wind; everywhere the edges of the wave crests are blown into froth; visibility affected.",
    landCriteria:
      "Very rarely experienced; accompanied by widespread damage.",
  },
  {
    force: 12,
    name: "Hurricane force",
    knots: { low: 64, high: Number.POSITIVE_INFINITY },
    probableWaveHeight: "14 m+",
    seaCriteria:
      "The air is filled with foam and spray; sea completely white with driving spray; visibility very seriously affected.",
    landCriteria:
      "Devastation.",
  },
];

export function categoryLabel(c: WeatherCategory): string {
  switch (c) {
    case "wind":
      return "Wind force";
    case "visibility":
      return "Visibility";
    case "sea-state":
      return "Sea state";
    case "timing":
      return "Timing";
    case "movement":
      return "Movement of systems";
    case "pressure":
      return "Pressure tendency";
  }
}
